import React, { useMemo, useState } from "react";
import * as XLSX from "xlsx";

const FacturacionComparativo = () => {
  const [excelBase, setExcelBase] = useState(null);
  const [excelAuxiliar, setExcelAuxiliar] = useState(null);
  const [procesando, setProcesando] = useState(false);
  const [procesado, setProcesado] = useState(false);
  const [progreso, setProgreso] = useState(0);
  const [pasoActual, setPasoActual] = useState("");
  const [errores, setErrores] = useState([]);
  const [filtroEstatus, setFiltroEstatus] = useState("Todos");
  const [busqueda, setBusqueda] = useState("");
  const [resultados, setResultados] = useState([]);

  const puedeProcesar = excelBase && excelAuxiliar;

  const formatMoney = (value) => {
    const number = Number(value || 0);

    return number.toLocaleString("es-MX", {
      style: "currency",
      currency: "MXN",
    });
  };

  const normalizarTexto = (value) => {
    return String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .toLowerCase();
  };

  const normalizarNota = (value) => {
    const text = String(value || "").toUpperCase();
    const match = text.match(/D\d{9}/);

    return match ? match[0] : text.trim();
  };

  const parseNumber = (value) => {
    if (value === null || value === undefined || value === "") return 0;

    if (typeof value === "number") return value;

    const clean = String(value)
      .replace(/\$/g, "")
      .replace(/,/g, "")
      .replace(/\s/g, "")
      .trim();

    const number = Number(clean);

    return Number.isNaN(number) ? 0 : number;
  };

  const formatDate = (value) => {
    if (!value) return "";

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return value.toLocaleDateString("es-MX");
    }

    if (typeof value === "number") {
      const parsed = XLSX.SSF.parse_date_code(value);

      if (parsed) {
        const day = String(parsed.d).padStart(2, "0");
        const month = String(parsed.m).padStart(2, "0");
        const year = parsed.y;

        return `${day}/${month}/${year}`;
      }
    }

    return String(value);
  };

  const validarArchivoExcel = (file) => {
    if (!file) return false;

    const extension = file.name.split(".").pop().toLowerCase();

    return ["xls", "xlsx", "csv"].includes(extension);
  };

  const leerExcel = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (event) => {
        try {
          const data = new Uint8Array(event.target.result);

          const workbook = XLSX.read(data, {
            type: "array",
            cellDates: true,
          });

          resolve(workbook);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => {
        reject(new Error("No se pudo leer el archivo."));
      };

      reader.readAsArrayBuffer(file);
    });
  };

  const obtenerRowsDePrimerSheet = (workbook) => {
    const firstSheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[firstSheetName];

    return XLSX.utils.sheet_to_json(sheet, {
      header: 1,
      defval: "",
      raw: false,
    });
  };

  const buscarHeaderRow = (rows, columnasRequeridas = []) => {
    for (let i = 0; i < rows.length; i += 1) {
      const row = rows[i] || [];
      const rowNormalizada = row.map((cell) => normalizarTexto(cell));

      const coincide = columnasRequeridas.every((columna) =>
        rowNormalizada.some((cell) => cell.includes(normalizarTexto(columna))),
      );

      if (coincide) {
        return i;
      }
    }

    return -1;
  };

  const crearIndexHeaders = (headers) => {
    const index = {};

    headers.forEach((header, position) => {
      const key = normalizarTexto(header);

      if (key) {
        index[key] = position;
      }
    });

    return index;
  };

  const getCellByHeader = (row, headersIndex, posiblesNombres = []) => {
    for (const nombre of posiblesNombres) {
      const key = normalizarTexto(nombre);

      if (headersIndex[key] !== undefined) {
        return row[headersIndex[key]];
      }
    }

    return "";
  };

  const obtenerMontoBase = (row, headersIndex) => {
    const precioSinIva = parseNumber(
      getCellByHeader(row, headersIndex, ["Precio sin IVA"]),
    );

    const utilidadFacturada = parseNumber(
      getCellByHeader(row, headersIndex, ["Utilidad Facturada"]),
    );

    const utilidadDesembolsada = parseNumber(
      getCellByHeader(row, headersIndex, ["utilidad desembolsada"]),
    );

    const utilidadNeta = parseNumber(
      getCellByHeader(row, headersIndex, ["Utilidad Neta"]),
    );

    if (precioSinIva > 0) return precioSinIva;
    if (utilidadFacturada > 0) return utilidadFacturada;
    if (utilidadDesembolsada > 0) return utilidadDesembolsada;
    if (utilidadNeta > 0) return utilidadNeta;

    return 0;
  };

  const procesarBaseOtrosIngresos = (workbook) => {
    const rows = obtenerRowsDePrimerSheet(workbook);

    const headerRowIndex = buscarHeaderRow(rows, [
      "Nota Cargo",
      "Tipo Producto",
      "Nombre Cliente",
    ]);

    if (headerRowIndex === -1) {
      throw new Error(
        "No se encontró el encabezado de la Base Otros Ingresos.",
      );
    }

    const headers = rows[headerRowIndex];
    const headersIndex = crearIndexHeaders(headers);
    const agrupado = {};

    for (let i = headerRowIndex + 1; i < rows.length; i += 1) {
      const row = rows[i] || [];

      const notaRaw = getCellByHeader(row, headersIndex, [
        "Nota Cargo o Factura",
        "Nota Cargo",
        "Factura",
      ]);

      const notaCargo = normalizarNota(notaRaw);

      if (!notaCargo || !/^D\d{9}$/.test(notaCargo)) {
        continue;
      }

      const montoBase = obtenerMontoBase(row, headersIndex);

      const fecha = formatDate(
        getCellByHeader(row, headersIndex, ["Fecha Factura"]),
      );

      const tipoProducto = getCellByHeader(row, headersIndex, [
        "Tipo Producto",
      ]);

      const descripcionProducto = getCellByHeader(row, headersIndex, [
        "Descripción del Producto",
        "Descripcion del Producto",
        "Descripción del Producto ",
      ]);

      const producto = [tipoProducto, descripcionProducto]
        .filter(Boolean)
        .join(" - ");

      const cliente = getCellByHeader(row, headersIndex, ["Nombre Cliente"]);
      const vin = getCellByHeader(row, headersIndex, ["No de Vin", "VIN"]);
      const facturaUnidad = getCellByHeader(row, headersIndex, [
        "Factura de unidad",
      ]);
      const vendedor = getCellByHeader(row, headersIndex, ["Nombre Vendedor"]);
      const vehiculo = getCellByHeader(row, headersIndex, ["Vehiculo"]);
      const broker = getCellByHeader(row, headersIndex, ["Broker"]);
      const proveedor = getCellByHeader(row, headersIndex, ["Proveedor"]);

      if (!agrupado[notaCargo]) {
        agrupado[notaCargo] = {
          notaCargo,
          fecha,
          cliente,
          vin,
          facturaUnidad,
          vendedor,
          vehiculo,
          broker,
          proveedor,
          productos: [],
          montoBase: 0,
          registrosBase: 0,
          detalleBase: [],
        };
      }

      agrupado[notaCargo].montoBase += montoBase;
      agrupado[notaCargo].registrosBase += 1;

      if (producto && !agrupado[notaCargo].productos.includes(producto)) {
        agrupado[notaCargo].productos.push(producto);
      }

      agrupado[notaCargo].detalleBase.push({
        fecha,
        tipoProducto,
        descripcionProducto,
        producto,
        cliente,
        vin,
        facturaUnidad,
        vendedor,
        vehiculo,
        broker,
        proveedor,
        montoBase,
      });
    }

    return agrupado;
  };

  const procesarAuxiliarContable = (workbook) => {
    const rows = obtenerRowsDePrimerSheet(workbook);

    const headerRowIndex = buscarHeaderRow(rows, [
      "FECHA",
      "CONCEPTO",
      "CARGOS",
      "ABONOS",
    ]);

    if (headerRowIndex === -1) {
      throw new Error("No se encontró el encabezado del Auxiliar Contable.");
    }

    const headers = rows[headerRowIndex];
    const headersIndex = crearIndexHeaders(headers);
    const agrupado = {};

    let cuentaActual = "";
    let nombreCuentaActual = "";

    for (let i = headerRowIndex + 1; i < rows.length; i += 1) {
      const row = rows[i] || [];

      const primeraCelda = normalizarTexto(row[0]);

      if (primeraCelda === "cuenta") {
        cuentaActual = row[1] || "";
        nombreCuentaActual = row[4] || "";
        continue;
      }

      const concepto = String(
        getCellByHeader(row, headersIndex, ["CONCEPTO"]) || "",
      );

      const notaMatch = concepto.toUpperCase().match(/D\d{9}/);

      if (!notaMatch) {
        continue;
      }

      const notaCargo = notaMatch[0];

      const fecha = formatDate(getCellByHeader(row, headersIndex, ["FECHA"]));
      const poliza = getCellByHeader(row, headersIndex, ["POLIZA"]);
      const numero = getCellByHeader(row, headersIndex, ["NUMERO"]);
      const cargos = parseNumber(getCellByHeader(row, headersIndex, ["CARGOS"]));
      const abonos = parseNumber(getCellByHeader(row, headersIndex, ["ABONOS"]));

      const montoAuxiliar = abonos - cargos;

      if (!agrupado[notaCargo]) {
        agrupado[notaCargo] = {
          notaCargo,
          fecha,
          montoAuxiliar: 0,
          registrosAuxiliar: 0,
          cuentas: [],
          conceptos: [],
          detalleAuxiliar: [],
        };
      }

      agrupado[notaCargo].montoAuxiliar += montoAuxiliar;
      agrupado[notaCargo].registrosAuxiliar += 1;

      if (
        nombreCuentaActual &&
        !agrupado[notaCargo].cuentas.includes(nombreCuentaActual)
      ) {
        agrupado[notaCargo].cuentas.push(nombreCuentaActual);
      }

      agrupado[notaCargo].conceptos.push(concepto);

      agrupado[notaCargo].detalleAuxiliar.push({
        fecha,
        poliza,
        numero,
        concepto,
        cuenta: cuentaActual,
        nombreCuenta: nombreCuentaActual,
        cargos,
        abonos,
        montoAuxiliar,
      });
    }

    return agrupado;
  };

  const generarComparativo = (baseAgrupada, auxiliarAgrupado) => {
    const notas = Array.from(
      new Set([
        ...Object.keys(baseAgrupada || {}),
        ...Object.keys(auxiliarAgrupado || {}),
      ]),
    ).sort();

    return notas.map((notaCargo) => {
      const base = baseAgrupada[notaCargo];
      const auxiliar = auxiliarAgrupado[notaCargo];

      const montoBase = Number(base?.montoBase || 0);
      const montoAuxiliar = Number(auxiliar?.montoAuxiliar || 0);
      const diferencia = Number((montoBase - montoAuxiliar).toFixed(2));
      const diferenciaAbs = Math.abs(diferencia);

      let estatus = "Conciliado";
      let observacion = "Importe coincide correctamente";

      if (base && !auxiliar) {
        estatus = "Sin auxiliar";
        observacion = "La nota existe en base, pero no se encontró en auxiliar";
      } else if (!base && auxiliar) {
        estatus = "Sin base";
        observacion = "La nota existe en auxiliar, pero no se encontró en base";
      } else if (diferenciaAbs > 0.5) {
        estatus = "Diferencia";
        observacion = "El importe del auxiliar no coincide con la base";
      }

      return {
        notaCargo,
        fecha: base?.fecha || auxiliar?.fecha || "",
        cliente: base?.cliente || "NO IDENTIFICADO",
        vin: base?.vin || "",
        facturaUnidad: base?.facturaUnidad || "",
        vendedor: base?.vendedor || "",
        vehiculo: base?.vehiculo || "",
        producto: base?.productos?.length
          ? base.productos.join(" / ")
          : "Movimiento contable",
        broker: base?.broker || "",
        proveedor: base?.proveedor || "",
        montoBase,
        montoAuxiliar,
        diferencia,
        estatus,
        observacion,
        registrosBase: base?.registrosBase || 0,
        registrosAuxiliar: auxiliar?.registrosAuxiliar || 0,
        cuentasAuxiliar: auxiliar?.cuentas?.join(" / ") || "",
      };
    });
  };

  const resetProceso = () => {
    setProcesado(false);
    setProcesando(false);
    setProgreso(0);
    setPasoActual("");
    setFiltroEstatus("Todos");
    setBusqueda("");
    setResultados([]);
  };

  const handleExcelBaseChange = (event) => {
    const file = event.target.files?.[0] || null;

    if (file && !validarArchivoExcel(file)) {
      setExcelBase(null);
      setErrores([`El archivo "${file.name}" no es un Excel válido.`]);
      resetProceso();
      return;
    }

    setExcelBase(file);
    setErrores([]);
    resetProceso();
  };

  const handleExcelAuxiliarChange = (event) => {
    const file = event.target.files?.[0] || null;

    if (file && !validarArchivoExcel(file)) {
      setExcelAuxiliar(null);
      setErrores([`El archivo "${file.name}" no es un Excel válido.`]);
      resetProceso();
      return;
    }

    setExcelAuxiliar(file);
    setErrores([]);
    resetProceso();
  };

  const limpiarArchivos = () => {
    setExcelBase(null);
    setExcelAuxiliar(null);
    setErrores([]);
    resetProceso();

    const inputs = document.querySelectorAll(".comparativo-file-input");

    inputs.forEach((input) => {
      input.value = "";
    });
  };

  const actualizarPaso = async (texto, porcentaje, tiempo = 400) => {
    setPasoActual(texto);
    setProgreso(porcentaje);

    await new Promise((resolve) => {
      setTimeout(resolve, tiempo);
    });
  };

  const procesarArchivos = async () => {
    if (!puedeProcesar) {
      setErrores([
        "Para procesar necesitas cargar la base de otros ingresos y el auxiliar contable.",
      ]);
      return;
    }

    try {
      setProcesando(true);
      setProcesado(false);
      setErrores([]);
      setResultados([]);
      setProgreso(0);

      await actualizarPaso("Leyendo base de otros ingresos", 20, 500);
      const workbookBase = await leerExcel(excelBase);

      await actualizarPaso("Analizando base de otros ingresos", 40, 500);
      const baseAgrupada = procesarBaseOtrosIngresos(workbookBase);

      await actualizarPaso("Leyendo auxiliar contable", 55, 500);
      const workbookAuxiliar = await leerExcel(excelAuxiliar);

      await actualizarPaso("Cruzando notas de cargo", 75, 600);
      const auxiliarAgrupado = procesarAuxiliarContable(workbookAuxiliar);

      await actualizarPaso("Calculando diferencias", 90, 500);
      const comparativo = generarComparativo(baseAgrupada, auxiliarAgrupado);

      await actualizarPaso("Generando resultado de conciliación", 100, 400);

      setResultados(comparativo);
      setProcesando(false);
      setProcesado(true);
      setPasoActual("Proceso finalizado");
    } catch (error) {
      console.error(error);

      setProcesando(false);
      setProcesado(false);
      setProgreso(0);
      setPasoActual("");

      setErrores([
        error?.message ||
          "Ocurrió un error al procesar los archivos. Revisa que correspondan a la base y al auxiliar.",
      ]);
    }
  };

  const resultadosFiltrados = useMemo(() => {
    return resultados.filter((item) => {
      const coincideEstatus =
        filtroEstatus === "Todos" || item.estatus === filtroEstatus;

      const textoBusqueda = busqueda.trim().toLowerCase();

      const coincideBusqueda =
        textoBusqueda === "" ||
        item.notaCargo.toLowerCase().includes(textoBusqueda) ||
        item.cliente.toLowerCase().includes(textoBusqueda) ||
        item.vin.toLowerCase().includes(textoBusqueda) ||
        item.facturaUnidad.toLowerCase().includes(textoBusqueda) ||
        item.producto.toLowerCase().includes(textoBusqueda) ||
        item.vendedor.toLowerCase().includes(textoBusqueda) ||
        item.cuentasAuxiliar.toLowerCase().includes(textoBusqueda);

      return coincideEstatus && coincideBusqueda;
    });
  }, [resultados, filtroEstatus, busqueda]);

  const resumen = useMemo(() => {
    const totalRegistros = resultados.length;

    const conciliados = resultados.filter(
      (item) => item.estatus === "Conciliado",
    ).length;

    const diferencias = resultados.filter(
      (item) => item.estatus === "Diferencia",
    ).length;

    const sinAuxiliar = resultados.filter(
      (item) => item.estatus === "Sin auxiliar",
    ).length;

    const sinBase = resultados.filter(
      (item) => item.estatus === "Sin base",
    ).length;

    const totalBase = resultados.reduce(
      (total, item) => total + Number(item.montoBase || 0),
      0,
    );

    const totalAuxiliar = resultados.reduce(
      (total, item) => total + Number(item.montoAuxiliar || 0),
      0,
    );

    const diferenciaTotal = resultados.reduce(
      (total, item) => total + Math.abs(Number(item.diferencia || 0)),
      0,
    );

    return {
      totalRegistros,
      conciliados,
      diferencias,
      sinAuxiliar,
      sinBase,
      totalBase,
      totalAuxiliar,
      diferenciaTotal,
    };
  }, [resultados]);

  const getBadgeClass = (estatus) => {
    switch (estatus) {
      case "Conciliado":
        return "badge bg-success text-white";
      case "Diferencia":
        return "badge bg-warning text-dark";
      case "Sin auxiliar":
        return "badge bg-danger text-white";
      case "Sin base":
        return "badge bg-dark text-white";
      default:
        return "badge bg-info text-white";
    }
  };

  const escapeExcelValue = (value) => {
    if (value === null || value === undefined) return "";

    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  };

  const obtenerFechaArchivo = () => {
    const fecha = new Date();

    const year = fecha.getFullYear();
    const month = String(fecha.getMonth() + 1).padStart(2, "0");
    const day = String(fecha.getDate()).padStart(2, "0");
    const hours = String(fecha.getHours()).padStart(2, "0");
    const minutes = String(fecha.getMinutes()).padStart(2, "0");

    return `${year}-${month}-${day}_${hours}-${minutes}`;
  };

  const descargarArchivo = (contenido, nombreArchivo, tipoArchivo) => {
    const blob = new Blob([contenido], {
      type: tipoArchivo,
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = nombreArchivo;
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const generarHtmlExcel = (titulo, contenidoTabla) => {
    return `
      <html xmlns:o="urn:schemas-microsoft-com:office:office"
            xmlns:x="urn:schemas-microsoft-com:office:excel"
            xmlns="http://www.w3.org/TR/REC-html40">
        <head>
          <meta charset="UTF-8" />
          <!--[if gte mso 9]>
            <xml>
              <x:ExcelWorkbook>
                <x:ExcelWorksheets>
                  <x:ExcelWorksheet>
                    <x:Name>${escapeExcelValue(titulo)}</x:Name>
                    <x:WorksheetOptions>
                      <x:DisplayGridlines/>
                    </x:WorksheetOptions>
                  </x:ExcelWorksheet>
                </x:ExcelWorksheets>
              </x:ExcelWorkbook>
            </xml>
          <![endif]-->
          <style>
            table {
              border-collapse: collapse;
              font-family: Arial, sans-serif;
              font-size: 12px;
            }

            th {
              background: #0f4c81;
              color: #ffffff;
              font-weight: bold;
              border: 1px solid #cccccc;
              padding: 8px;
              text-align: left;
            }

            td {
              border: 1px solid #cccccc;
              padding: 8px;
            }

            .number {
              text-align: right;
            }

            .title {
              font-size: 18px;
              font-weight: bold;
              color: #0f4c81;
              border: none;
              padding: 10px 0;
            }

            .subtitle {
              color: #555555;
              border: none;
              padding: 0 0 12px 0;
            }

            .total {
              font-weight: bold;
              background: #f3f6f9;
            }
          </style>
        </head>

        <body>
          ${contenidoTabla}
        </body>
      </html>
    `;
  };

  const aplicarAnchosColumnas = (worksheet, anchos = []) => {
  worksheet["!cols"] = anchos.map((wch) => ({ wch }));
};

const aplicarFormatoMoneda = (worksheet, columnas = [], totalFilas = 0) => {
  columnas.forEach((columna) => {
    for (let row = 2; row <= totalFilas; row += 1) {
      const cellRef = `${columna}${row}`;

      if (worksheet[cellRef]) {
        worksheet[cellRef].t = "n";
        worksheet[cellRef].z = "$#,##0.00";
      }
    }
  });
};

const aplicarFormatoNumero = (worksheet, columnas = [], totalFilas = 0) => {
  columnas.forEach((columna) => {
    for (let row = 2; row <= totalFilas; row += 1) {
      const cellRef = `${columna}${row}`;

      if (worksheet[cellRef]) {
        worksheet[cellRef].t = "n";
        worksheet[cellRef].z = "#,##0";
      }
    }
  });
};

const descargarExcel = () => {
  const registros = resultadosFiltrados;

  if (!registros.length) {
    setErrores(["No hay registros para descargar con los filtros aplicados."]);
    return;
  }

  const totalBase = registros.reduce(
    (total, item) => total + Number(item.montoBase || 0),
    0,
  );

  const totalAuxiliar = registros.reduce(
    (total, item) => total + Number(item.montoAuxiliar || 0),
    0,
  );

  const totalDiferencia = registros.reduce(
    (total, item) => total + Math.abs(Number(item.diferencia || 0)),
    0,
  );

  const datosExcel = registros.map((row) => ({
    "Nota cargo": row.notaCargo,
    Fecha: row.fecha,
    Cliente: row.cliente,
    VIN: row.vin || "-",
    "Factura unidad": row.facturaUnidad || "-",
    Vendedor: row.vendedor || "-",
    Vehículo: row.vehiculo || "-",
    Producto: row.producto,
    Broker: row.broker || "-",
    Proveedor: row.proveedor || "-",
    "Monto base": Number(row.montoBase || 0),
    "Monto auxiliar": Number(row.montoAuxiliar || 0),
    Diferencia: Number(row.diferencia || 0),
    Estatus: row.estatus,
    "Registros base": Number(row.registrosBase || 0),
    "Registros auxiliar": Number(row.registrosAuxiliar || 0),
    "Cuentas auxiliar": row.cuentasAuxiliar || "-",
    Observación: row.observacion,
  }));

  datosExcel.push({
    "Nota cargo": "TOTALES",
    Fecha: "",
    Cliente: "",
    VIN: "",
    "Factura unidad": "",
    Vendedor: "",
    Vehículo: "",
    Producto: "",
    Broker: "",
    Proveedor: "",
    "Monto base": Number(totalBase.toFixed(2)),
    "Monto auxiliar": Number(totalAuxiliar.toFixed(2)),
    Diferencia: Number(totalDiferencia.toFixed(2)),
    Estatus: "",
    "Registros base": "",
    "Registros auxiliar": "",
    "Cuentas auxiliar": "",
    Observación: "",
  });

  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet(datosExcel);

  aplicarAnchosColumnas(worksheet, [
    16, // Nota cargo
    14, // Fecha
    36, // Cliente
    22, // VIN
    18, // Factura unidad
    28, // Vendedor
    24, // Vehículo
    42, // Producto
    22, // Broker
    24, // Proveedor
    16, // Monto base
    18, // Monto auxiliar
    16, // Diferencia
    16, // Estatus
    16, // Registros base
    18, // Registros auxiliar
    32, // Cuentas auxiliar
    45, // Observación
  ]);

  const totalFilas = datosExcel.length + 1;

  aplicarFormatoMoneda(worksheet, ["K", "L", "M"], totalFilas);
  aplicarFormatoNumero(worksheet, ["O", "P"], totalFilas);

  XLSX.utils.book_append_sheet(workbook, worksheet, "Comparativo");

  const fechaArchivo = obtenerFechaArchivo();

  XLSX.writeFile(workbook, `comparativo_excel_${fechaArchivo}.xlsx`);
};

const descargarResumen = () => {
  const datosResumen = [
    ["Indicador", "Resultado"],
    ["Registros procesados", resumen.totalRegistros],
    ["Conciliados", resumen.conciliados],
    ["Diferencias", resumen.diferencias],
    ["Sin auxiliar", resumen.sinAuxiliar],
    ["Sin base", resumen.sinBase],
    ["Total base", Number(resumen.totalBase.toFixed(2))],
    ["Total auxiliar", Number(resumen.totalAuxiliar.toFixed(2))],
    ["Diferencia total", Number(resumen.diferenciaTotal.toFixed(2))],
  ];

  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.aoa_to_sheet(datosResumen);

  aplicarAnchosColumnas(worksheet, [28, 20]);

  const totalFilas = datosResumen.length;

  aplicarFormatoMoneda(worksheet, ["B"], totalFilas);

  XLSX.utils.book_append_sheet(workbook, worksheet, "Resumen");

  const fechaArchivo = obtenerFechaArchivo();

  XLSX.writeFile(workbook, `resumen_comparativo_${fechaArchivo}.xlsx`);
};

  return (
    <div>
      <div className="d-flex flex-wrap align-items-center justify-content-between mb-3 gap-2">
        <div>
          <h1 className="page-header mb-1">Comparativo Excel</h1>
          <p className="text-muted mb-0">
            Conciliación entre base de otros ingresos y auxiliar contable.
          </p>
        </div>

        <button className="btn btn-outline-secondary" onClick={limpiarArchivos}>
          Limpiar archivos
        </button>
      </div>

      {errores.length > 0 && (
        <div className="alert alert-warning">
          {errores.map((error, index) => (
            <div key={index}>{error}</div>
          ))}
        </div>
      )}

      <div className="row">
        <div className="col-xl-6 col-lg-6">
          <div className="card border-0 mb-3">
            <div className="card-body">
              <div className="mb-3">
                <h5 className="card-title mb-1">Base Otros Ingresos</h5>
                <p className="text-muted small mb-0">
                  Archivo principal con notas de cargo, cliente, VIN, producto,
                  utilidad e importes.
                </p>
              </div>

              <input
                type="file"
                className="form-control comparativo-file-input"
                accept=".xls,.xlsx,.csv"
                onChange={handleExcelBaseChange}
              />

              <div className="mt-3">
                <span
                  className={excelBase ? "badge bg-success" : "badge bg-secondary"}
                >
                  {excelBase ? "Archivo cargado" : "Pendiente"}
                </span>
              </div>

              {excelBase && (
                <div className="mt-3 small text-muted text-truncate">
                  {excelBase.name}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-xl-6 col-lg-6">
          <div className="card border-0 mb-3">
            <div className="card-body">
              <div className="mb-3">
                <h5 className="card-title mb-1">Auxiliar Contable</h5>
                <p className="text-muted small mb-0">
                  Archivo para validar movimientos, cargos, abonos y notas de
                  cargo registradas.
                </p>
              </div>

              <input
                type="file"
                className="form-control comparativo-file-input"
                accept=".xls,.xlsx,.csv"
                onChange={handleExcelAuxiliarChange}
              />

              <div className="mt-3">
                <span
                  className={
                    excelAuxiliar ? "badge bg-success" : "badge bg-secondary"
                  }
                >
                  {excelAuxiliar ? "Archivo cargado" : "Pendiente"}
                </span>
              </div>

              {excelAuxiliar && (
                <div className="mt-3 small text-muted text-truncate">
                  {excelAuxiliar.name}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="card border-0 mb-3">
        <div className="card-body">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
            <div>
              <h5 className="mb-1">Procesamiento</h5>
              <p className="text-muted mb-0">
                El sistema cruzará las notas de cargo de ambos archivos y
                calculará diferencias.
              </p>
            </div>

            <button
              className="btn btn-primary"
              disabled={!puedeProcesar || procesando}
              onClick={procesarArchivos}
            >
              {procesando ? "Procesando..." : "Procesar comparativo"}
            </button>
          </div>

          <div className="row">
            <div className="col-md-6">
              <div className="border rounded p-3 mb-2">
                <div className="text-muted small">Base Otros Ingresos</div>
                <h4 className="mb-0">{excelBase ? 1 : 0}</h4>
              </div>
            </div>

            <div className="col-md-6">
              <div className="border rounded p-3 mb-2">
                <div className="text-muted small">Auxiliar Contable</div>
                <h4 className="mb-0">{excelAuxiliar ? 1 : 0}</h4>
              </div>
            </div>
          </div>

          {(procesando || procesado) && (
            <div className="mt-3">
              <div className="d-flex justify-content-between mb-1">
                <span className="text-muted small">{pasoActual}</span>
                <span className="text-muted small">{progreso}%</span>
              </div>

              <div className="progress" style={{ height: "8px" }}>
                <div
                  className="progress-bar"
                  role="progressbar"
                  style={{ width: `${progreso}%` }}
                  aria-valuenow={progreso}
                  aria-valuemin="0"
                  aria-valuemax="100"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {procesado && (
        <>
          <div className="row">
            <div className="col-xl-3 col-md-6">
              <div className="card border-0 mb-3 facturacion-kpi-card">
                <div className="card-body">
                  <div className="text-muted small mb-1">
                    Registros procesados
                  </div>
                  <div className="facturacion-kpi-value">
                    {resumen.totalRegistros}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-3 col-md-6">
              <div className="card border-0 mb-3 facturacion-kpi-card">
                <div className="card-body">
                  <div className="text-muted small mb-1">Conciliados</div>
                  <div className="facturacion-kpi-value text-success">
                    {resumen.conciliados}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-3 col-md-6">
              <div className="card border-0 mb-3 facturacion-kpi-card">
                <div className="card-body">
                  <div className="text-muted small mb-1">
                    Revisión requerida
                  </div>
                  <div className="facturacion-kpi-value text-warning">
                    {resumen.diferencias +
                      resumen.sinAuxiliar +
                      resumen.sinBase}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-3 col-md-6">
              <div className="card border-0 mb-3 facturacion-kpi-card">
                <div className="card-body">
                  <div className="text-muted small mb-1">Diferencia total</div>
                  <div className="facturacion-kpi-value facturacion-kpi-money">
                    {formatMoney(resumen.diferenciaTotal)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card border-0 mb-3">
            <div className="card-body">
              <div className="row">
                <div className="col-md-3">
                  <div className="border rounded p-3 mb-2 facturacion-summary-box">
                    <div className="text-muted small">Total base</div>
                    <h5 className="mb-0">{formatMoney(resumen.totalBase)}</h5>
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="border rounded p-3 mb-2 facturacion-summary-box">
                    <div className="text-muted small">Total auxiliar</div>
                    <h5 className="mb-0">
                      {formatMoney(resumen.totalAuxiliar)}
                    </h5>
                  </div>
                </div>

                <div className="col-md-2">
                  <div className="border rounded p-3 mb-2 facturacion-summary-box">
                    <div className="text-muted small">Diferencias</div>
                    <h5 className="mb-0">{resumen.diferencias}</h5>
                  </div>
                </div>

                <div className="col-md-2">
                  <div className="border rounded p-3 mb-2 facturacion-summary-box">
                    <div className="text-muted small">Sin auxiliar</div>
                    <h5 className="mb-0">{resumen.sinAuxiliar}</h5>
                  </div>
                </div>

                <div className="col-md-2">
                  <div className="border rounded p-3 mb-2 facturacion-summary-box">
                    <div className="text-muted small">Sin base</div>
                    <h5 className="mb-0">{resumen.sinBase}</h5>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card border-0">
            <div className="card-body">
              <div className="d-flex flex-wrap align-items-center justify-content-between mb-3 gap-2">
                <div>
                  <h5 className="mb-1">Resultado del comparativo</h5>
                  <p className="text-muted mb-0">
                    Revisión de coincidencias, diferencias y registros no
                    localizados entre archivos.
                  </p>
                </div>

                <div className="d-flex flex-wrap gap-2 ms-auto">
                  <button
                    type="button"
                    className="btn btn-primary btn-sm px-3"
                    onClick={descargarExcel}
                  >
                    Descargar Excel
                  </button>

                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm px-3"
                    onClick={descargarResumen}
                  >
                    Descargar resumen
                  </button>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-4">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Buscar por nota, cliente, VIN, factura o producto"
                    value={busqueda}
                    onChange={(event) => setBusqueda(event.target.value)}
                  />
                </div>

                <div className="col-md-3">
                  <select
                    className="form-select"
                    value={filtroEstatus}
                    onChange={(event) => setFiltroEstatus(event.target.value)}
                  >
                    <option value="Todos">Todos los estatus</option>
                    <option value="Conciliado">Conciliado</option>
                    <option value="Diferencia">Diferencia</option>
                    <option value="Sin auxiliar">Sin auxiliar</option>
                    <option value="Sin base">Sin base</option>
                  </select>
                </div>
              </div>

              <div className="table-responsive facturacion-table-wrapper">
                <table
                  className="table table-hover align-middle facturacion-table"
                  style={{ minWidth: "1650px" }}
                >
                  <thead className="facturacion-table-head">
                    <tr>
                      <th>Nota cargo</th>
                      <th>Fecha</th>
                      <th>Cliente</th>
                      <th>VIN</th>
                      <th>Factura unidad</th>
                      <th>Producto</th>
                      <th className="text-end">Monto base</th>
                      <th className="text-end">Monto auxiliar</th>
                      <th className="text-end">Diferencia</th>
                      <th>Estatus</th>
                      <th>Base</th>
                      <th>Auxiliar</th>
                      <th>Observación</th>
                    </tr>
                  </thead>

                  <tbody>
                    {resultadosFiltrados.length > 0 ? (
                      resultadosFiltrados.map((row, index) => (
                        <tr key={index}>
                          <td>{row.notaCargo}</td>
                          <td>{row.fecha}</td>
                          <td className="col-cliente">{row.cliente}</td>
                          <td>{row.vin || "-"}</td>
                          <td>{row.facturaUnidad || "-"}</td>
                          <td className="col-producto">{row.producto}</td>
                          <td className="text-end">
                            {formatMoney(row.montoBase)}
                          </td>
                          <td className="text-end">
                            {formatMoney(row.montoAuxiliar)}
                          </td>
                          <td className="text-end">
                            {formatMoney(row.diferencia)}
                          </td>
                          <td>
                            <span className={getBadgeClass(row.estatus)}>
                              {row.estatus}
                            </span>
                          </td>
                          <td>{row.registrosBase}</td>
                          <td>{row.registrosAuxiliar}</td>
                          <td className="col-observacion">
                            {row.observacion}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="13" className="text-center text-muted py-4">
                          No se encontraron registros con los filtros aplicados.
                        </td>
                      </tr>
                    )}
                  </tbody>

                  <tfoot>
                    <tr>
                      <th colSpan="6">Totales</th>
                      <th className="text-end">
                        {formatMoney(
                          resultadosFiltrados.reduce(
                            (total, item) =>
                              total + Number(item.montoBase || 0),
                            0,
                          ),
                        )}
                      </th>
                      <th className="text-end">
                        {formatMoney(
                          resultadosFiltrados.reduce(
                            (total, item) =>
                              total + Number(item.montoAuxiliar || 0),
                            0,
                          ),
                        )}
                      </th>
                      <th className="text-end">
                        {formatMoney(
                          resultadosFiltrados.reduce(
                            (total, item) =>
                              total + Math.abs(Number(item.diferencia || 0)),
                            0,
                          ),
                        )}
                      </th>
                      <th colSpan="4" />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default FacturacionComparativo;